// Phase 3 — Passive commercial-payment policy visibility badge.
//
// Pure helper consumed by:
//   • Quotation list (from frozen quotation fields)
//   • Case header (from `data.commercial_payment_policy`)
//   • Cases list (from additive list-response fields)
//
// Returns null when nothing meaningful can be derived (no commercial
// snapshot, no mode, etc.) so callers can render conditionally without
// extra null-guards.
//
// Output shape:
//   {
//     label: "ADV-60",
//     tone:  "cyan",   // matches existing chip styling vocabulary
//     title: "Advance 60% before documentation",
//     kind:  "STATIC" | "LIVE",   // LIVE = warning/blocker derived from policy
//   }
//
// The helper NEVER calls the API and NEVER mutates input. Backwards-
// compatible: when `payment_term_mode` is missing it derives the mode
// from `advance_percent_before_documentation`.

function _mode(src) {
  const m = (src.payment_term_mode || src.mode || "").toUpperCase();
  if (m === "REGULAR" || m === "ADVANCE_PERCENT" || m === "DUE_DAYS") return m;
  const adv = Number(src.advance_percent_before_documentation ?? src.advance_percent ?? 0);
  if (adv > 0) return "ADVANCE_PERCENT";
  return "REGULAR";
}

function _formatAed(n) {
  const v = Number(n || 0);
  return `AED ${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

// Live state (warnings / blockers) — only relevant when `src` is the
// commercial-payment-policy contract (Case Detail) or the cases-list
// derived block. Quotation rows never carry live state.
function _liveBadge(src) {
  const blocker = (src.blocker_code || src.payment_policy_blocker_code || "").toUpperCase();
  const warn = (src.warning_code || src.payment_policy_warning_code || "").toUpperCase();
  const days = src.days_until_due ?? src.payment_days_until_due ?? null;
  const due = src.due_date || src.payment_due_date || null;
  const outstanding = src.outstanding_amount ?? src.commercial_policy_outstanding_amount ?? null;
  if (blocker === "PAYMENT_OVERDUE") {
    return {
      label: outstanding != null ? `Overdue · ${_formatAed(outstanding)}` : "Overdue",
      tone: "rose",
      title: due ? `Payment overdue (due ${due}). Workflow blocked until paid.` : "Payment overdue. Workflow blocked until paid.",
      kind: "LIVE",
    };
  }
  if (blocker === "INVOICE_REQUIRED") {
    return { label: "Issue invoice", tone: "cyan",
             title: "DUE_DAYS workflow needs an issued invoice before Stage 2.", kind: "LIVE" };
  }
  if (blocker === "ISSUE_INVOICE") {
    return { label: "Issue draft", tone: "cyan",
             title: "Draft invoice exists — issue it to start the due-date clock.", kind: "LIVE" };
  }
  if (warn === "PAYMENT_DUE_TODAY") {
    return { label: "Due today", tone: "amber",
             title: "Payment due today. Workflow continues until overdue.", kind: "LIVE" };
  }
  if (warn === "PAYMENT_DUE_SOON" && Number.isFinite(Number(days))) {
    return {
      label: `Due in ${days} day${Number(days) === 1 ? "" : "s"}`,
      tone: "amber",
      title: due ? `Payment due ${due}.` : "Payment due soon.",
      kind: "LIVE",
    };
  }
  return null;
}

export function getCommercialPolicyBadge(src) {
  if (!src) return null;
  // Cases list / quotation list shorthand: a `commercial_payment_badge`
  // field already pre-formatted by the backend short-circuits the
  // derivation. Live state still wins so badges stay current.
  const live = _liveBadge(src);
  if (live) return live;
  const mode = _mode(src);
  if (mode === "REGULAR") {
    return { label: "REGULAR", tone: "slate",
             title: "Regular payment term — no pre-documentation gate.",
             kind: "STATIC" };
  }
  if (mode === "ADVANCE_PERCENT") {
    const adv = Number(src.advance_percent_before_documentation ?? src.advance_percent ?? 0);
    if (!adv) return null;
    return { label: `ADV-${String(adv).padStart(2, "0")}`, tone: "cyan",
             title: `Advance ${adv}% required before documentation.`,
             kind: "STATIC" };
  }
  // DUE_DAYS
  const dd = src.payment_due_days;
  if (dd == null) return null;
  return { label: `DUE-${String(dd).padStart(2, "0")}`, tone: "blue",
           title: `Payment due within ${dd} day${Number(dd) === 1 ? "" : "s"} of invoice issue.`,
           kind: "STATIC" };
}

// Tailwind class string for the supported tones. Centralised so all
// three surfaces render with identical chip styling.
export const COMMERCIAL_POLICY_BADGE_CLASSES = {
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  cyan:  "bg-cyan-50 text-cyan-800 border-cyan-200",
  blue:  "bg-blue-50 text-blue-800 border-blue-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  rose:  "bg-rose-50 text-rose-800 border-rose-200",
  green: "bg-emerald-50 text-emerald-800 border-emerald-200",
};
